import { z } from 'zod/v3';
import { AssetType, AssetTypeSchema } from '../generated/enums';

// Metadata schema for video assets
export const VideoAssetMetadataSchema = z.object({
  originalName: z.string().optional(),
  originalUrl: z.string().optional(),
  uploadedUrl: z.string().optional(),
  dimensions: z.object({
    width: z.number(),
    height: z.number()
  }).optional(),
  duration: z.number().optional(), // Duration in seconds for video/audio
  format: z.string().optional(),
  frameRate: z.number().optional(),
  bitrate: z.number().optional(),
  codec: z.string().optional(),

  // URL reference fields
  isUrlReference: z.boolean().optional(),
  validatedAt: z.string().optional()
}).passthrough(); // Allow additional properties

// Main VideoAsset DTO
export const VideoAssetDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  type: AssetTypeSchema,
  name: z.string(),
  url: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
  metadata: VideoAssetMetadataSchema.nullable().optional(),
  size: z.number(), // Size in bytes
  mimeType: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: z.string().nullable().optional()
});

// Create VideoAsset input schema
export const CreateVideoAssetDTOSchema = z.object({
  fileName: z.string(),
  contentType: z.string(),
  size: z.number(),
  base64Data: z.string()
});

// Create VideoAsset from URL input schema
export const CreateVideoAssetFromUrlDTOSchema = z.object({
  url: z.string().url(),
  name: z.string().optional()
});

// List VideoAssets query schema
export const ListVideoAssetsDTOSchema = z.object({
  type: AssetTypeSchema.optional(),
  limit: z.number().default(100),
  offset: z.number().default(0)
});

// Uploaded asset response type (for frontend upload service)
export const UploadedAssetSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  url: z.string(),
  fileSize: z.number(),
  contentType: z.string(),
  metadata: z.record(z.any()),
  type: z.string(), // Lowercase string version of AssetType
  method: z.enum(['direct', 'url']),
  origin: z.enum(['user', 'system']),
  status: z.enum(['uploaded', 'failed', 'pending']),
  isPreview: z.boolean()
});

// Type exports for use in TypeScript
export type VideoAssetMetadata = z.infer<typeof VideoAssetMetadataSchema>;
export type VideoAssetDTO = z.infer<typeof VideoAssetDTOSchema>;
export type CreateVideoAssetDTO = z.infer<typeof CreateVideoAssetDTOSchema>;
export type CreateVideoAssetFromUrlDTO = z.infer<typeof CreateVideoAssetFromUrlDTOSchema>;
export type ListVideoAssetsDTO = z.infer<typeof ListVideoAssetsDTOSchema>;
export type UploadedAsset = z.infer<typeof UploadedAssetSchema>;